import { useState, useEffect, useCallback } from "react";
import ColasService from "../api/colas.api";

const MENSAJE_ERROR_CARGA = "No se pudieron cargar las colas";

/**
 * Hook para cargar la lista de colas y gestionar su alta, edición y borrado.
 * No genera toasts: los mutadores relanzan el error para que la página lo muestre.
 * @returns {{
 *   colas: Array,
 *   loading: boolean,
 *   guardando: boolean,
 *   error: string|null,
 *   crearCola: (nombre: string) => Promise<Object>,
 *   editarCola: (id: number|string, nombre: string) => Promise<Object>,
 *   eliminarCola: (id: number|string) => Promise<void>,
 *   recargar: () => Promise<void>
 * }} Estado y operaciones de colas
 */
export const useColas = () => {
    const [colas, setColas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);

    // Única lógica de carga (efecto inicial y recargar). `estaCancelado` permite
    // al efecto descartar la respuesta si el componente se desmontó
    const cargar = useCallback(async (estaCancelado = () => false) => {
        setLoading(true);
        setError(null);
        try {
            const data = await ColasService.list();
            if (!estaCancelado()) setColas(data);
        } catch (err) {
            console.error("Error al cargar colas:", err);
            if (!estaCancelado()) setError(err.message || MENSAJE_ERROR_CARGA);
        } finally {
            if (!estaCancelado()) setLoading(false);
        }
    }, []);

    const recargar = useCallback(() => cargar(), [cargar]);

    useEffect(() => {
        let cancelled = false;
        cargar(() => cancelled);
        return () => {
            cancelled = true;
        };
    }, [cargar]);

    // Envuelve una mutación con el estado `guardando`; el error se relanza
    const mutar = useCallback(async (operacion) => {
        setGuardando(true);
        try {
            return await operacion();
        } finally {
            setGuardando(false);
        }
    }, []);

    const crearCola = useCallback(
        (nombre) =>
            mutar(async () => {
                const creada = await ColasService.create({ nombre });
                setColas((prev) => [...prev, creada]);
                return creada;
            }),
        [mutar]
    );

    const editarCola = useCallback(
        (id, nombre) =>
            mutar(async () => {
                const actualizada = await ColasService.update(id, { nombre });
                setColas((prev) => prev.map((cola) => (cola.id === id ? actualizada : cola)));
                return actualizada;
            }),
        [mutar]
    );

    const eliminarCola = useCallback(
        (id) =>
            mutar(async () => {
                await ColasService.delete(id);
                setColas((prev) => prev.filter((cola) => cola.id !== id));
            }),
        [mutar]
    );

    return { colas, loading, guardando, error, crearCola, editarCola, eliminarCola, recargar };
};
